import { Box, Typography, Avatar, useMediaQuery, useTheme } from '@mui/material';

import ProjectTypeIcon from '../Icon/ProjectTypeIcon';
import AreaIcon from '../Icon/AreaIcon';
import TenureIcon from '../Icon/TenureIcon';
import CompletedIcon from '../Icon/CompletedIcon';
import ParkingIcon from '../Icon/ParkingIcon';
import StatusIcon from '../Icon/StatusIcon';
import UnitIcon from '../Icon/UnitIcon';
import _ from 'lodash';
import { formatDateByLocale } from "../../utils/utils";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const useResponsiveIconSize = () => {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  if (isLg) return 40;
  if (isSm) return 36;
  return 24;
};

const labelFontSize = { xs: '0.9rem', sm: '1rem', lg: '1.15rem' };
const valueFontSize = { xs: '1.1rem', sm: '1.25rem', lg: '1.35rem' };
const unitFontSize = valueFontSize;

const InfoCol = ({ children }) => (
  <Box
    sx={{
      flex: '1 1 340px',
      minWidth: { xs: '100%', md: 0 },
      px: 2,
    }}
  >
    {children}
  </Box>
);

export const InfoRow = ({ icon, label, value }: any) => (
  <Box display="flex" alignItems="center" gap={2} mb={value ? 3 : 0}>
    {icon && (
      <Box
        sx={{
          minWidth: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: (theme) => theme.palette.grey[500],
        }}
      >
        {icon}
      </Box>
    )}
    <Box>
      <Typography variant="body1" color="#8D8D8D" sx={{ fontSize: labelFontSize, fontWeight: 400 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: valueFontSize,
          color: (theme) => theme.palette.primary.main,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const UnitSizeRow = ({ type, series, size, unit }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexDirection: 'row',
      flexWrap: 'wrap',
      mb: 0.5,
      py: 0.5,
      maxWidth: 400,
    }}
  >
    <Typography
      variant="body2"
      sx={{
        mr: 1,
        width: 'auto',
        fontSize: unitFontSize,
      }}
    >
      {type} {series}
    </Typography>
    <Typography variant="body2" sx={{ fontSize: unitFontSize }}>
      <Box component="span" sx={{ color: (theme) => theme.palette.primary.main, pr: 1 }}>
        {size}
      </Box>
      {unit}
    </Typography>
  </Box>
);

export const ProjectInfo = ({ projectInfo, projectDetail, units, t, lang }) => {
  const iconSize = useResponsiveIconSize();
  const getLocalized = (field) => projectInfo?.[`${field}_${lang}`] || t('not-specified');

  const renderQuantityValue = () => {
    const hasFloor = !!projectInfo?.No_of_Floor_TH;
    const hasUnit = !!projectInfo?.No_of_Unit;

    if (!hasFloor && !hasUnit) {
      return <Box component="span">{t('not-specified')}</Box>;
    }

    return (
      <>
        {hasFloor && (
          <Box component="span" sx={{ color: (theme) => theme.palette.primary.main }}>
            {projectInfo.No_of_Floor_TH}
          </Box>
        )}
        {hasFloor && (
          <Box component="span" sx={{ color: 'common.black' }}>
            {` ${t('floor-unit')} `}
          </Box>
        )}
        {hasUnit && (
          <Box component="span" sx={{ color: (theme) => theme.palette.primary.main }}>
            {projectInfo.No_of_Unit}
          </Box>
        )}
        {hasUnit && (
          <Box component="span" sx={{ color: 'common.black' }}>
            {` ${t('unit')} `}
          </Box>
        )}
      </>
    );
  };

  const renderParkingValue = () => {
    const isDefined = !!projectInfo?.No_of_Parking;

    return (
      <>
        <Box component="span" sx={{ color: (theme) => theme.palette.primary.main }}>
          {isDefined ? projectInfo?.No_of_Parking : t('not-specified')}
        </Box>
        {isDefined && (
          <Box component="span" sx={{ color: 'common.black' }}>
            {` ${t('car-unit')}`}
          </Box>
        )}
      </>
    );
  };

  const renderLandSizeValue = () => {
    const landSize = getLocalized('Land_Size');
    const parts = landSize.match(/(\d+\.\d+|\d+)|([^\d\s]+)/g) || [];

    if (_.isEqual(landSize, t('not-specified'))) {
      return <Box component="span">{t('not-specified')}</Box>;
    }

    return (
      <Box component="span">
        {parts.map((part, index) => {
          const isNumber = /^\d+(\.\d+)?$/.test(part);
          return (
            <Box component="span" key={index} sx={{ color: isNumber ? 'primary.main' : 'common.black' }}>
              {part + ' '}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <section>
      {/* Main info */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
        <InfoCol>
          <InfoRow
            icon={<ProjectTypeIcon width={iconSize} height={iconSize} />}
            label={t('project-type')}
            value={`${projectInfo?.category} ${projectInfo?.type}` || t('not-specified')}
          />
          <InfoRow
            icon={<AreaIcon width={iconSize} height={iconSize} />}
            label={t('land-size')}
            value={renderLandSizeValue()}
          />
          <InfoRow
            icon={<TenureIcon width={iconSize} height={iconSize} />}
            label={t('tenure')}
            value={getLocalized('Tenure')}
          />
          <InfoRow
            icon={<CompletedIcon width={iconSize} height={iconSize} />}
            label={t('completed')}
            value={
              projectDetail?.Built
                ? formatDateByLocale(projectDetail?.Built, lang.toLowerCase())
                : t('not-specified')
            }
          />
        </InfoCol>
        <InfoCol>
          <InfoRow
            icon={<ProjectTypeIcon width={iconSize} height={iconSize} />}
            label={t('quantity')}
            value={renderQuantityValue()}
          />
          <InfoRow
            icon={<ParkingIcon width={iconSize} height={iconSize} />}
            label={t('parking')}
            value={renderParkingValue()}
          />
          <InfoRow
            icon={<StatusIcon width={iconSize} height={iconSize} />}
            label={t('Status')}
            value={projectInfo?.status || t('not-specified')}
          />
        </InfoCol>
      </Box>

      {/* Room size */}
      <Box>
        <InfoRow
          icon={<UnitIcon width={iconSize} height={iconSize} fill="#9F9FA0" />}
          label={t('roomTypeAndSize')}
          value={units?.length === 0 && <Box sx={{ color: 'common.black' }}>{t('not-specified')}</Box>}
        />
        <Box sx={{ mx: { xs: 6, sm: 7 }, display: 'flex', flexDirection: 'column' }}>
          {units?.map((item, index) => (
            <UnitSizeRow key={index} {...item} />
          ))}
        </Box>
      </Box>

      {/* Developer */}
      <Box display="flex" gap={2} mt={2} mb={4} alignContent={'center'}>
        <Avatar
          src={projectInfo?.developed_image || ''}
          alt={projectInfo?.developed}
          variant="rounded"
          sx={{
            width: { xs: 50, md: 55, lg: 65 },
            height: { xs: 50, md: 55, lg: 65 },
            bgcolor: '#fff',
            boxShadow: '0px 2px 10px 0px #0000001A',
            '& img': {
              objectFit: 'contain',
            },
          }}
        >
          <ProjectTypeIcon width={iconSize} height={iconSize} />
        </Avatar>

        <InfoRow
          label={t('developed-by')}
          value={
            <Box component={'span'} sx={{ color: 'common.black' }}>
              {projectInfo?.developed || '-'}
            </Box>
          }
        />
      </Box>
    </section>
  );
};
